// Libraries
import { logEvent as firebaseLogEvent, setUserId, setUserProperties } from 'firebase/analytics'

// Services
import api from '../api'
import store from '../store'

// Utilities
import firebase from './firebaseWebUtil'
import environment from './environmentUtils'
import isEmpty from './isEmpty'
import logger from './loggerUtil'
import preferences from './preferenceUtil'
import { AnalyticsConstants, RegexConstants } from '../constants'

class AnalyticsUtil {
  static async init () {
    await firebase.init()
    // This is to mimic the 'App Launched' event that fires for the native apps.
    AnalyticsUtil.logEvent(AnalyticsConstants.eventName.portalLaunched, undefined, { logMarketingEvent: true })
  }

  static setScreenName (screenName) {
    AnalyticsUtil.logEvent(AnalyticsConstants.eventName.screenView, { firebase_screen: screenName })
    store.commit('display/updateHeader', { ariaHeader: screenName })
  }

  /**
   * Set Firebase identity based on the current ProfileId from cognito.
  */
  static async setUserId (id) {
    try {
      logger.debug(`Saving User Id Property: ${id}`)

      const analytics = await firebase.getAnalyticInstance()

      if (!isEmpty(analytics)) {
        setUserId(analytics, id, { global: true })
      }
    } catch (e) {
      logger.error('Error setting analytics User ID')
      logger.debug(e)
    }
  }

  /**
   * Set an analytics user property.
  */
  static async setUserProperty (name, value) {
    try {
      logger.debug(`Setting analytics user property: ${name}=${value}`)

      // Firebase
      const analytics = await firebase.getAnalyticInstance()

      // Iterable
      const cognitoId = store.getters['session/getCognitoId']
      const email = store.getters['customer/customerEmail']
      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        api.analytics.updateIterableProfile(email, { [name]: value }).catch(e => {
          logger.error(`Error setting user property ${name}, to Iterable`)
          logger.debug(e)
        })
      }

      if (!isEmpty(analytics)) {
        setUserProperties(analytics, { [name.toString().toLowerCase()]: value.toString().toLowerCase() })
      }
    } catch (e) {
      logger.error(`Error setting analytics user property ${name}`)
      logger.debug(e)
    }
  }

  static async logEvent (name, data, options) {
    data ??= {}

    try {
      const analytics = await firebase.getAnalyticInstance()

      data.environment = environment.environment
      data.site_brand = process.env.VUE_APP_AppBrand
      data.app_brand = process.env.VUE_APP_AppBrand
      data.application_type = 'servicing'

      const vergentCustomerId = store.getters['customer/tlxeCustomerId']
      const profileId = store.getters['session/getCognitoId']
      const customerType = store.getters['applicationCustomer/customerTypeAnalytics']

      if (!isEmpty(vergentCustomerId)) {
        data[AnalyticsConstants.propName.mobileTLXeCustomerId.toLowerCase()] = vergentCustomerId
      }

      if (!isEmpty(profileId)) {
        data.profile_id = profileId
      }

      if (!isEmpty(customerType)) {
        data.customer_type = customerType
      }

      if (!isEmpty(store.state.applicationCustomer.applicationId)) {
        data.application_id = store.state.applicationCustomer.applicationId
      }

      const sessionId = preferences.getSessionId()

      if (!isEmpty(sessionId)) {
        data.custom_session_id = sessionId
      }

      logger.debug(`Logging event ${name} with options ${JSON.stringify(data)}`)

      if (!isEmpty(analytics)) {
        firebaseLogEvent(analytics, name, data, { global: true })
      }
    } catch (e) {
      logger.error(`Error attempting to log event ${name}`)
      logger.debug(e)
    }

    if (!isEmpty(options) && options.logMarketingEvent === true) {
      AnalyticsUtil.logMarketingEvent(name, data)
    }
  }

  static logMarketingEvent (name, data) {
    AnalyticsUtil.recordIterableEvent(name, data)
  }

  static recordIterableEvent (eventName, properties = {}) {
    try {
      const cognitoId = store.getters['session/getCognitoId']
      const email = store.getters['customer/customerEmail']

      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        api.analytics.logIterableEvent(email, properties, eventName).catch(e => {
          logger.error(`Error recording event ${eventName}, to Iterable`)
          logger.debug(e)
        })
      }
    } catch (e) {
      logger.error('Error logging Iterable event')
      logger.debug(e)
    }
  }

  static sendCustomerAnalytics () {
    try {
      const vergentCustomerId = store.getters['customer/tlxeCustomerId']

      if (!isEmpty(vergentCustomerId)) {
        AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.mobileTLXeCustomerId, vergentCustomerId)
      }

      if (store.state.display.isPwa) {
        AnalyticsUtil.setUserProperty(AnalyticsConstants.eventName.pwaInstalled, true)
        AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.isPwa, true)
        AnalyticsUtil.logEvent(AnalyticsConstants.eventName.pwaInstalled, { value: true }, { logMarketingEvent: true })
        AnalyticsUtil.logEvent(AnalyticsConstants.eventName.joinGroup, { value: AnalyticsConstants.itemType.progressive_web_app }, { logMarketingEvent: true })
      }

      AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.isMobileGenerated, true)
    } catch (e) {
      logger.error('Unable to set customerId user properties')
      logger.debug(e)
    }
  }

  /**
   * Sets the site-wide, initialization related user properties.
   */
  static async saveInitUserProperties () {
    await AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.environment, environment.environment)
    await AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.siteBrand, process.env.VUE_APP_AppBrand)
  }

  /**
   * Updates the users identity in various analytics platforms.
   */
  static async updateIdentity (newEmail = undefined) {
    logger.debug('Setting Marketing Identity')
    try {
      const vergentCustomerId = store.getters['customer/tlxeCustomerId']
      const hasVergentCustomerId = !isEmpty(vergentCustomerId)
      const cognitoId = store.getters['session/getCognitoId']
      const email = !isEmpty(newEmail) ? newEmail : store.getters['customer/customerEmail']
      const phoneNumber = store.state.customer.phoneNumber?.phoneNumber?.replaceAll(RegexConstants.phoneNumberFormatting, '').slice(-10) // must be numbers only for Content Square (and remove potential country code)

      store.dispatch('customer/updateMdmId').catch(e => {
        logger.error('Error getting MDM')
        logger.debug(e)
      })

      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        await api.analytics.createIterableProfile(email)
      }

      await AnalyticsUtil.setUserId(cognitoId)
      await AnalyticsUtil.saveInitUserProperties()

      if (hasVergentCustomerId) {
        await AnalyticsUtil.setUserProperty(AnalyticsConstants.userProperties.mobileTLXeCustomerId, vergentCustomerId)
      }

      if (!isEmpty(email)) {
        await AnalyticsUtil.setUserProperty(AnalyticsConstants.userProperties.email, email)
      }

      if (!isEmpty(phoneNumber)) {
        await AnalyticsUtil.setUserProperty(AnalyticsConstants.userProperties.phoneNumber, phoneNumber)
      }

      await AnalyticsUtil.setUserProperty(AnalyticsConstants.userProperties.isMobileGenerated, true)
    } catch (e) {
      logger.error('Error on analyticsUtil.updateIdentity')
      logger.debug(e)
    }
  }
}

export default AnalyticsUtil
